import { type LazyExoticComponent, lazy } from 'react';

// React.lazy's own upper bound is ComponentType<any>; deriving it from lazy's
// signature reuses that exact bound without writing `any` (banned by lint), so
// the wrapper preserves each screen's prop types (e.g. <Project create />).
type AnyComponentType = Awaited<ReturnType<Parameters<typeof lazy>[0]>>['default'];

const RELOAD_KEY = 'lazy-chunk-reloaded-at';
// If we already reloaded within this window, a repeat failure is genuine
// (a real module-eval bug, or the server is actually down) — stop reloading
// so we surface the error instead of looping.
const RELOAD_WINDOW_MS = 10_000;

/**
 * Drop-in replacement for React.lazy that survives stale chunks after a deploy.
 *
 * Screens are code-split, so a tab holding the previous index.html requests a
 * chunk hash that no longer exists on the server. The SPA fallback answers the
 * 404 with index.html (MIME text/html), the dynamic import() rejects with
 * "error loading dynamically imported module", and — with no error boundary
 * around the route's Suspense — the app crashes.
 *
 * On the first such failure we force one full reload, which fetches a fresh
 * index.html with current chunk hashes so the import resolves. A sessionStorage
 * timestamp caps this at a single reload per window, so a genuine module error
 * can't turn into a reload loop.
 */
export function lazyWithRetry<T extends AnyComponentType>(factory: () => Promise<{ default: T }>): LazyExoticComponent<T> {
    return lazy(() => retryDynamicImport(factory));
}

/**
 * Runs a dynamic-import factory; on failure forces one full reload (guarded so a
 * genuine module error can't loop). Exported for tests — {@link lazyWithRetry}
 * is the intended entry point.
 */
export async function retryDynamicImport<R>(factory: () => Promise<R>): Promise<R> {
    try {
        return await factory();
    } catch (error) {
        const lastReload = Number(window.sessionStorage.getItem(RELOAD_KEY));
        const reloadedRecently = lastReload > 0 && Date.now() - lastReload < RELOAD_WINDOW_MS;
        if (!reloadedRecently) {
            window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
            window.location.reload();
            // The reload replaces this document — never resolve, so React keeps
            // showing the Suspense fallback rather than flashing an error.
            return new Promise<R>(() => {});
        }
        throw error;
    }
}
